'use client';

import { useState, useEffect } from "react";

/**
 * Offer strip with a live 12-hour countdown. Three horizontal parts:
 *   1. "Limited-time offer" with the countdown clock below it
 *   2. "N report credits free" headline
 *   3. Sign up button with "no credit card required" below it
 * The deadline is stored in localStorage so the countdown survives refreshes
 * within the 12h window; when it reaches zero it automatically restarts for
 * another 12h (a repeating countdown). Time renders client-side
 * (suppressHydrationWarning) to avoid an SSR mismatch.
 */

const DURATION_MS = 12 * 60 * 60 * 1000; // 12 hours
const STORAGE_KEY = "dxr_offer_deadline";

export default function CountdownStrip({ freeReports = 100, appUrl = "#" }) {
  const [left, setLeft] = useState(DURATION_MS);

  useEffect(() => {
    // Start a brand-new 12h window (also persisted so refreshes resume it).
    const freshDeadline = () => {
      const d = Date.now() + DURATION_MS;
      try { localStorage.setItem(STORAGE_KEY, String(d)); } catch (e) {}
      return d;
    };

    let deadline;
    try {
      const stored = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      deadline = stored && stored > Date.now() ? stored : freshDeadline();
    } catch (e) {
      deadline = Date.now() + DURATION_MS;
    }

    const tick = () => {
      let remaining = deadline - Date.now();
      if (remaining <= 0) {
        // Reached zero — restart the 12h countdown and keep looping.
        deadline = freshDeadline();
        remaining = deadline - Date.now();
      }
      setLeft(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const total = Math.max(0, Math.floor(left / 1000));
  const hh = String(Math.floor(total / 3600)).padStart(2, '0');
  const mm = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');

  return (
    <div className="dob-strip">
      {/* 1 — offer label + countdown clock */}
      <div className="dob-strip-part dob-strip-offer">
        <span className="dob-strip-eyebrow">Limited-time offer</span>
        <div className="dob-strip-timer">
          <span className="dob-strip-ends">Ends in</span>
          <span className="dob-strip-clock" suppressHydrationWarning aria-label={`Offer ends in ${hh}:${mm}:${ss}`}>
            <span className="t-unit">{hh}</span><i>:</i><span className="t-unit">{mm}</span><i>:</i><span className="t-unit">{ss}</span>
          </span>
        </div>
      </div>

      {/* 2 — credits headline (the hero) */}
      <div className="dob-strip-part dob-strip-credits">
        <span className="dob-strip-headline">
          {freeReports} report credits <b>free</b>
        </span>
      </div>

      {/* 3 — sign up + note */}
      <div className="dob-strip-part dob-strip-action">
        <a className="dob-btn dob-btn-primary dob-strip-signup" href={appUrl}>Sign up →</a>
        <span className="dob-strip-note">* No credit card required</span>
      </div>
    </div>
  );
}
